import React from "react";
import { CheckCircle2, Loader2, Wrench, XCircle } from "lucide-react";
import type { ExecuteStreamEvent, HealStage } from "@/services/testing";
import { ActivityDot } from "@/components/atoms/testing/ActivityDot";
import { RunElapsed } from "@/components/atoms/testing/RunElapsed";
import { RunningStepBanner } from "@/components/molecules/testing/RunningStepBanner";
import { activityKindLabel } from "@/utils/testing/planDisplay";
import { healStageLabel } from "./HealProposalCard";

// ─── SVG animations ──────────────────────────────────────────────────────────

export function AnimatedCheck() {
	return (
		<svg
			viewBox="0 0 24 24"
			className="h-4 w-4 text-success-strong"
			fill="none"
			stroke="currentColor"
			strokeWidth={2.5}
		>
			<circle cx="12" cy="12" r="10" className="opacity-20" />
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M6 12.5l4 4 8-8"
				style={{
					strokeDasharray: 20,
					strokeDashoffset: 20,
					animation: "draw 0.4s ease forwards 0.1s",
				}}
			/>
			<style>{`@keyframes draw { to { stroke-dashoffset: 0; } }`}</style>
		</svg>
	);
}

export function AnimatedX() {
	return (
		<svg
			viewBox="0 0 24 24"
			className="h-4 w-4 text-destructive-strong"
			fill="none"
			stroke="currentColor"
			strokeWidth={2.5}
		>
			<circle cx="12" cy="12" r="10" className="opacity-20" />
			<path
				strokeLinecap="round"
				d="M8 8l8 8M16 8l-8 8"
				style={{
					strokeDasharray: 24,
					strokeDashoffset: 24,
					animation: "draw 0.3s ease forwards 0.05s",
				}}
			/>
			<style>{`@keyframes draw { to { stroke-dashoffset: 0; } }`}</style>
		</svg>
	);
}

// ─── Live execution feed ──────────────────────────────────────────────────────

export const EVENT_LABEL: Partial<Record<ExecuteStreamEvent["type"], string>> = {
	run_started: "Browser launched",
	browser_complete: "Browser run complete",
	narrating: "Narrating result…",
	triaging: "Triaging failure…",
	questioning: "Generating question…",
};

// ─── Heal proposal progress (heal_proposal_started / heal_progress) ──────────
//
// The old `healing_started` / `healing` events claimed the system was repairing itself mid-run.
// It never was — the heal stream diagnoses and PROPOSES, and the user decides. So this reports
// "preparing a fix proposal", with the pipeline stage it has reached.

export function detectHealProgress(
	events: ExecuteStreamEvent[],
	isActive: boolean,
): { stage?: HealStage; attempt?: number; maxAttempts?: number; detail?: string; triageCategory?: string } | null {
	if (!isActive) return null;
	let started: { triageCategory?: string } | null = null;
	let latest: { stage?: HealStage; attempt?: number; maxAttempts?: number; detail?: string } | null = null;
	for (const e of events) {
		if (e.type === "heal_proposal_started") started = { triageCategory: e.triage_category };
		else if (e.type === "heal_progress")
			latest = { stage: e.stage, attempt: e.attempt, maxAttempts: e.max_attempts, detail: e.detail };
		else if (e.type === "heal_proposal" || e.type === "complete") return null; // resolved
	}
	if (!started && !latest) return null;
	return { ...(latest ?? {}), triageCategory: started?.triageCategory };
}

export function HealProgressBanner({
	stage,
	attempt,
	maxAttempts,
	detail,
	triageCategory,
}: {
	stage?: HealStage;
	attempt?: number;
	maxAttempts?: number;
	detail?: string;
	triageCategory?: string;
}) {
	return (
		<div className="relative mb-2 overflow-hidden rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
			<div
				data-tst-decorative-motion
				className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-warning/25 to-transparent"
			/>
			<div className="relative flex items-center gap-2">
				<Wrench className="h-3.5 w-3.5 shrink-0 animate-pulse text-warning-strong" />
				<span className="shrink-0 font-semibold text-warning-strong">Preparing fix proposal</span>
				<span className="min-w-0 truncate text-warning-strong/90">
					{healStageLabel(stage)}
					{detail ? ` — ${detail}` : ""}
				</span>
				{attempt != null && maxAttempts != null && maxAttempts > 1 && (
					<span className="shrink-0 rounded bg-warning/20 px-1.5 py-0.5 font-mono text-[11px] text-warning-strong">
						{attempt}/{maxAttempts}
					</span>
				)}
				{triageCategory && (
					<span className="shrink-0 rounded border border-warning/30 px-1.5 py-0.5 font-mono text-[11px] text-warning-strong/80">
						{triageCategory}
					</span>
				)}
				<span
					data-tst-decorative-motion
					className="ml-auto h-1.5 w-1.5 shrink-0 animate-ping rounded-full bg-warning"
				/>
			</div>
		</div>
	);
}

// ─── Main execution feed ──────────────────────────────────────────────────────

export function ExecutionFeed({
	events,
	isActive,
	hideFrame,
	expanded,
	startedAt,
}: {
	events: ExecuteStreamEvent[];
	isActive: boolean;
	hideFrame?: boolean;
	expanded?: boolean;
	startedAt?: number | null;
}) {
	const feedRef = React.useRef<HTMLDivElement>(null);
	React.useEffect(() => {
		feedRef.current?.scrollTo({
			top: feedRef.current.scrollHeight,
			behavior: "smooth",
		});
	}, [events.length]);

	// Latest screenshot = the Live Browser view; latest progress/step = status line.
	let frame: string | null = null;
	let phase = "";
	let stepsBegun = 0;
	let stepsEnded = 0;
	for (const e of events) {
		if (e.type === "screenshot" && e.jpeg_base64)
			frame = `data:image/jpeg;base64,${e.jpeg_base64}`;
		else if (e.type === "progress" && e.phase) phase = e.phase;
		else if (e.type === "step" && e.phase === "begin") {
			phase = e.title;
			stepsBegun++;
		} else if (e.type === "step" && e.phase === "end") stepsEnded++;
	}
	// The step currently executing is the next one after all completed steps.
	const currentStep = isActive ? Math.max(stepsEnded + 1, stepsBegun) : stepsEnded;

	return (
		<div
			className={expanded ? "flex min-h-0 flex-1 flex-col gap-2" : "space-y-2"}
		>
			{(() => {
				const heal = detectHealProgress(events, isActive);
				return heal ? <HealProgressBanner {...heal} /> : null;
			})()}
			{phase && (
				<RunningStepBanner title={phase} active={isActive} stepNum={currentStep} startedAt={startedAt} />
			)}

			{/* Live Browser — screenshot stream of the cloud run (hidden when VNC video is shown) */}
			{!hideFrame && (
				<div className="relative aspect-video w-full overflow-hidden rounded-md border border-border bg-black/90">
					{frame ? (
						// biome-ignore lint/a11y/useAltText: live screenshot frame
						<img
							src={frame}
							alt="Live test browser"
							className="h-full w-full object-contain"
						/>
					) : (
						<div className="flex h-full w-full items-center justify-center text-[11px] text-white/70">
							{isActive ? (
								<span className="flex items-center gap-1.5">
									<Loader2 className="h-3 w-3 animate-spin" /> Launching
									browser…
								</span>
							) : (
								"Live browser view"
							)}
						</div>
					)}
					{phase && (
						<div className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-2 py-1 text-[11px] text-white">
							{isActive && (
								<span
									data-tst-decorative-motion
									className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-success align-middle"
								/>
							)}
							{phase}
						</div>
					)}
				</div>
			)}

			<div
				ref={feedRef}
				className={`min-h-[60px] overflow-y-auto rounded-md border border-border bg-card/40 px-2 py-1.5 font-mono text-[11px] leading-relaxed ${
					expanded ? "max-h-[40vh] flex-1" : "max-h-48"
				}`}
			>
				{events.map((e, i) => {
					// Is this a step that is currently executing?
					// True when: run is active, phase=begin, no 'end' for this title after index i,
					// and no later 'begin' for the same title (so only the last invocation is active).
					let isActiveStep = false;
					if (isActive && e.type === "step" && e.phase === "begin") {
						const rest = events.slice(i + 1);
						const hasEnd = rest.some((x) => x.type === "step" && x.phase === "end" && x.title === e.title);
						const hasLaterBegin = rest.some((x) => x.type === "step" && x.phase === "begin" && x.title === e.title);
						isActiveStep = !hasEnd && !hasLaterBegin;
					}
					if (e.type === "test_result") {
						return (
							<div key={i} className="flex items-center gap-1.5 py-0.5">
								{e.status === "passed" ? <AnimatedCheck /> : <AnimatedX />}
								<span
									className={
										e.status === "passed" ? "text-success-strong" : "text-destructive-strong"
									}
								>
									{e.title}
								</span>
								<span className="ml-auto text-muted-foreground">
									{e.duration_ms}ms
								</span>
							</div>
						);
					}
					if (e.type === "heal_proposal_started") {
						return (
							<div key={i} className="flex items-center gap-1.5 py-0.5 text-warning-strong">
								<Wrench className="h-3 w-3 animate-pulse" />
								<span>Looking for a fix to propose</span>
								<span className="truncate text-muted-foreground">— {e.triage_category}</span>
							</div>
						);
					}
					if (e.type === "heal_progress") {
						return (
							<div key={i} className="flex items-center gap-1.5 py-0.5 text-warning-strong">
								<Wrench className="h-3 w-3" />
								<span>{healStageLabel(e.stage)}</span>
								{e.attempt != null && e.max_attempts != null && e.max_attempts > 1 && (
									<span className="text-muted-foreground">
										({e.attempt}/{e.max_attempts})
									</span>
								)}
								{e.detail && (
									<span className="truncate text-muted-foreground">— {e.detail}</span>
								)}
							</div>
						);
					}
					if (e.type === "error") {
						return (
							<div
								key={i}
								className="flex items-center gap-1.5 py-0.5 text-destructive-strong"
							>
								<XCircle className="h-3 w-3" />
								<span>{e.message}</span>
							</div>
						);
					}
					if (e.type === "screenshot") return null; // shown as the Live Browser frame
					if (e.type === "step") {
						if (e.phase !== "begin") return null;
						// Check if this step has already completed
						const isDone = events
							.slice(i + 1)
							.some((x) => x.type === "step" && x.phase === "end" && x.title === e.title);
						return (
							<div
								key={i}
								className={`relative flex items-center gap-1.5 overflow-hidden rounded py-0.5 transition-colors ${
									isActiveStep
										? "bg-primary/10 px-1.5 text-foreground"
										: isDone
											? "text-foreground/40"
											: "text-foreground/70"
								}`}
							>
								{/* Shimmer sweep on the active step */}
								{isActiveStep && (
									<div
											data-tst-decorative-motion
											className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_1.8s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-primary/10 to-transparent"
										/>
								)}
								{isActiveStep ? (
									<span className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center">
										<span
												data-tst-decorative-motion
												className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60"
											/>
										<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
									</span>
								) : isDone ? (
									<CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-success-strong/60" />
								) : (
									<span className="text-primary/40">▶</span>
								)}
								<span className={`truncate ${isActiveStep ? "font-medium" : ""}`}>
									{e.title}
								</span>
								{isActiveStep && (
									<span className="ml-auto flex shrink-0 gap-0.5">
										{[0, 1, 2].map((j) => (
											<span
												key={j}
												data-tst-decorative-motion
												className="h-1 w-1 animate-bounce rounded-full bg-primary"
												style={{ animationDelay: `${j * 0.12}s` }}
											/>
										))}
									</span>
								)}
							</div>
						);
					}
					if (e.type === "api") {
						const bad = Number(e.status) >= 400;
						return (
							<div
								key={i}
								className="flex items-center gap-1.5 py-0.5 text-muted-foreground"
							>
								<span className="font-semibold">{e.method}</span>
								<span className="truncate">{e.resource}</span>
								<span
									className={`ml-auto ${bad ? "text-destructive-strong" : "text-success-strong"}`}
								>
									{e.status}
								</span>
							</div>
						);
					}
					if (e.type === "runtime_activity") {
						const blocked = e.status === "blocked";
						const bad = e.status === "error";
						// Amber: the step answered, but the answer is not usable evidence.
						const warn = e.status === "warning";
						return (
							<div
								key={i}
								className={`flex items-start gap-1.5 rounded px-1 py-1 ${
									blocked
										? "bg-primary/10 text-primary"
										: bad
											? "bg-destructive/10 text-destructive-strong"
											: warn
												? "bg-warning/10 text-warning-strong"
												: "text-muted-foreground"
								}`}
							>
								<ActivityDot
									status={e.status}
									streaming={isActive && e.status === "running"}
								/>
								<div className="min-w-0 flex-1">
									<div className="flex min-w-0 items-center gap-1.5">
										<span className="rounded border border-border/70 bg-background/50 px-1 py-0.5 text-[11px]">
											{activityKindLabel(e.kind)}
										</span>
										<span className="truncate text-foreground/90">{e.label}</span>
									</div>
									{e.detail && (
										<div className="mt-0.5 truncate text-muted-foreground">
											{e.detail}
										</div>
									)}
								</div>
							</div>
						);
					}
					const label = EVENT_LABEL[e.type];
					if (!label) return null;
					const isLast = i === events.length - 1;
					return (
						<div
							key={i}
							className="flex items-center gap-1.5 py-0.5 text-muted-foreground"
						>
							{isLast && isActive ? (
								<Loader2 className="h-3 w-3 animate-spin text-primary" />
							) : (
								<span className="h-3 w-3 text-center text-[8px] leading-3 text-muted-foreground/50">
									›
								</span>
							)}
							<span className={isLast && isActive ? "text-foreground" : ""}>
								{label}
							</span>
						</div>
					);
				})}
				{isActive && events.length === 0 && (
					<div className="flex items-center gap-1.5 py-0.5 text-muted-foreground">
						<Loader2 className="h-3 w-3 animate-spin text-primary" />
						<span>Initialising…</span>
					</div>
				)}
			</div>
		</div>
	);
}

// ─── Live step feed (left panel during execution) ────────────────────────────

export function RunLiveFeed({
	events,
	isActive,
	startedAt,
}: {
	events: ExecuteStreamEvent[];
	isActive: boolean;
	startedAt?: number | null;
}) {
	const stepResults = events.filter((e) => e.type === "test_result") as Array<{
		type: "test_result"; title: string; status: "passed" | "failed"; duration_ms: number; error?: string;
	}>;
	const currentStep = events
		.slice()
		.reverse()
		.find((e) => e.type === "step" && (e as Record<string, unknown>).phase === "begin");
	const currentStepTitle = currentStep ? String((currentStep as Record<string, unknown>).title ?? "") : "";
	const passedCount = stepResults.filter((s) => s.status === "passed").length;
	const failedCount = stepResults.filter((s) => s.status === "failed").length;

	return (
		<div className="rounded-md border border-primary/30 bg-card/80 p-2 text-xs shadow-sm">
			{/* Header */}
			<div className="mb-2 flex items-center gap-2">
				{isActive ? (
					<span className="relative flex h-2.5 w-2.5 shrink-0">
						<span
												data-tst-decorative-motion
												className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60"
											/>
						<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
					</span>
				) : (
					failedCount > 0 ? <XCircle className="h-3.5 w-3.5 text-destructive-strong" /> : <CheckCircle2 className="h-3.5 w-3.5 text-success-strong" />
				)}
				<span className="font-semibold text-foreground">
					{isActive ? "Running test…" : failedCount > 0 ? "Run complete — failures found" : "Run complete"}
				</span>
				<span className="ml-auto flex items-center gap-2">
					{isActive && startedAt != null && <RunElapsed startedAt={startedAt} reassure />}
					{stepResults.length > 0 && (
						<span className="font-mono text-[11px] text-muted-foreground">
							{passedCount}✓ {failedCount > 0 && <span className="text-destructive-strong">{failedCount}✗</span>}
						</span>
					)}
				</span>
			</div>
			{/* Fix-proposal banner — the chat-side feed otherwise shows nothing while the healer
			    diagnoses and verifies a candidate patch, making it look dead. */}
			{(() => {
				const heal = detectHealProgress(events, isActive);
				return heal ? <HealProgressBanner {...heal} /> : null;
			})()}
			{/* Current step */}
			{isActive && currentStepTitle && (
				<div className="mb-1.5 truncate text-[11px] text-muted-foreground">
					<span className="text-primary">▶</span> {currentStepTitle}
				</div>
			)}
			{/* Completed steps */}
			{stepResults.length > 0 && (
				<div className="space-y-0.5 border-t border-border/40 pt-1.5">
					{stepResults.map((s, i) => (
						<div key={i} className="flex items-start gap-1.5">
							{s.status === "passed" ? (
								<CheckCircle2 className="mt-0.5 h-2.5 w-2.5 shrink-0 text-success-strong" />
							) : (
								<XCircle className="mt-0.5 h-2.5 w-2.5 shrink-0 text-destructive-strong" />
							)}
							<span className={`truncate ${s.status === "passed" ? "text-foreground/80" : "font-medium text-destructive-strong"}`}>
								{s.title}
							</span>
							<span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground/60">
								{s.duration_ms}ms
							</span>
						</div>
					))}
				</div>
			)}
			{isActive && stepResults.length === 0 && (
				<div className="space-y-1.5 pt-1">
					<div data-tst-decorative-motion className="h-1.5 w-3/4 animate-pulse rounded bg-primary/20" />
					<div data-tst-decorative-motion className="h-1.5 w-1/2 animate-pulse rounded bg-primary/20" />
				</div>
			)}
		</div>
	);
}
